import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  noticeAddModalRecoil,
  noticeModifyModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import NoticeAddModal from '@/modal/NoticeAddModal';
import NoticeModifyModal from '@/modal/NoticeModifyModal';
import UserDivisionModifyModal from '@/modal/UserDivisionModifyModal';
import { NotcieApi } from '@/sdk/NotcieApi';
import {
  NoticeControllerGetNoticesParams,
  DeleteNoticeDtoTx,
  Notices,
} from '@/sdk/data-contracts';
import { noticeListRecoil } from '@/atom/NoticeAtom';
import SiteModifyModal from '@/modal/SiteModifyModal';

const NoticeList = () => {
  const notcieApi = new NotcieApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [noticeAddModalState, setNoticeAddModalState] =
    useRecoilState(noticeAddModalRecoil);
  const [noticeModifyModalState, setNoticeModifyModalState] = useRecoilState(
    noticeModifyModalRecoil,
  );
  const [noticeListState, setNoticeListState] =
    useRecoilState(noticeListRecoil);

  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');
  const [checkItems, setCheckItems] = useState<number[]>([]);
  const [listLength, setListLength] = useState<number>(0);
  const [modifyNum, setModifyNum] = useState<number>(0);

  const allCheckBtnRef = useRef<HTMLInputElement>(null);

  const downloadCSV = async (saveFileName: string) => {
    let allList: Notices[] | undefined;

    const dto: NoticeControllerGetNoticesParams = {
      sortBy,
      order,
    };

    try {
      const { data } = await notcieApi.noticeControllerGetNotices(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.results) allList = data.results;
    } catch (err: any) {}

    if (!allList || allList.length === 0) {
      setErrorModalText('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = ['No', '제목', '등록일', '작성자(ID)', '조회수'];

    const rows = allList.map((list, index) => [
      index + 1,
      list.title,
      list.createdAt,
      list.writer,
      list.viewCount,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const bom = '\uFEFF';
    const csvWithBom = bom + csvContent;

    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${saveFileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filterNoticeList = async () => {
    const dto: NoticeControllerGetNoticesParams = {
      title,
      offset,
      limit,
      sortBy,
      order,
    };

    try {
      const { data } = await notcieApi.noticeControllerGetNotices(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.results) setNoticeListState(data.results);
      if (data.totalColumn) {
        setListLength(data.totalColumn);
      } else if (data.totalColumn === 0) {
        setListLength(0);
      }
    } catch (err: any) {}
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckItems([]);
    setOffset(0);
    filterNoticeList();
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
    setCheckItems([]);
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleSingleCheck = (checked: boolean, id: number) => {
    if (checked) {
      setCheckItems((prev) => [...prev, id]);
    } else {
      setCheckItems((prev) => prev.filter((el) => el !== id));
    }
  };

  const handleAllCheck = (checked: boolean) => {
    if (checked) {
      const idArray: number[] = [];
      noticeListState?.forEach((el) => idArray.push(Number(el.id)));
      setCheckItems(idArray);
    } else {
      setCheckItems([]);
    }
  };

  const handleSelectDeleteNumCheck = () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }
    setDeleteModalState(true);
  };

  const handleSelectDelete = async () => {
    try {
      const dto: DeleteNoticeDtoTx = {
        ids: checkItems,
      };
      await notcieApi.noticeControllerDeleteNotice(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    filterNoticeList();
  }, [limit, offset]);

  return (
    <>
      <CntBox1>
        <TitleTextBox text="공지사항 목록" />
        <form onSubmit={handleSearch}>
          <SearchOptionBox>
            <InputWrap>
              <InputArea>
                <IS.TextInputType1
                  style={{ width: 320 }}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="제목"
                />
              </InputArea>
            </InputWrap>
            <SearchBtn />
          </SearchOptionBox>
        </form>
        <TableOpitonBox>
          <h1>
            전체 <span>{listLength.toLocaleString()}</span>
          </h1>
          <div className="right-box">
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type5
              type="button"
              onClick={() => downloadCSV('notice_list')}
            >
              Excel 다운로드
              <span className="img-box">
                <img src="/img/ico_down_fff.svg" alt="" />
              </span>
            </BS.Btn1Type5>
            <BS.Btn1Type1
              type="button"
              onClick={() => setNoticeAddModalState(true)}
            >
              등록
            </BS.Btn1Type1>
            <BS.Btn1Type2 type="button" onClick={handleSelectDeleteNumCheck}>
              선택 삭제
            </BS.Btn1Type2>
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>
                  <IS.CheckBoxType1>
                    <input
                      type="checkbox"
                      id="all"
                      onChange={(e) => handleAllCheck(e.target.checked)}
                      ref={allCheckBtnRef}
                      checked={
                        checkItems.length === noticeListState?.length &&
                        noticeListState?.length !== 0
                          ? true
                          : false
                      }
                    />
                    <label htmlFor="all" className="img-box">
                      <img src="/img/ico_check_1.svg" alt="" />
                    </label>
                  </IS.CheckBoxType1>
                </th>
                <th>No</th>
                <th>제목</th>
                <th>등록일</th>
                <th>작성자(ID)</th>
                <th>조회수</th>
              </tr>
            </thead>
            <tbody>
              {noticeListState?.map((list, index) => (
                <tr key={index}>
                  <td>
                    <IS.CheckBoxType1>
                      <input
                        type="checkbox"
                        id={`cv-${index}`}
                        onChange={(e) =>
                          handleSingleCheck(e.target.checked, Number(list.id))
                        }
                        checked={
                          checkItems.includes(Number(list.id)) ? true : false
                        }
                      />
                      <label htmlFor={`cv-${index}`} className="img-box">
                        <img src="/img/ico_check_1.svg" alt="" />
                      </label>
                    </IS.CheckBoxType1>
                  </td>
                  <td>{(offset + index + 1).toLocaleString()}</td>
                  <td
                    className="button-text"
                    onClick={() => {
                      setModifyNum(list.id);
                      setNoticeModifyModalState(true);
                    }}
                  >
                    {list.title}
                  </td>
                  <td>{list.createdAt}</td>
                  <td>{list.writer}</td>
                  <td>{list.viewCount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {listLength !== 0 && (
          <Pagenation
            listAllLength={listLength}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </CntBox1>
      {noticeAddModalState && <NoticeAddModal />}
      {noticeModifyModalState && <NoticeModifyModal id={modifyNum} />}
      {deleteModalState && <DeleteModal onConfirm={handleSelectDelete} />}
    </>
  );
};

export default NoticeList;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const ReportBox = styled.div`
  margin-top: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;

export const CheckWrap = styled(CS.Gap24Box)`
  margin-left: 16px;
`;
